// src/pages/Competitions/competitionsListingUtils.js

export function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

export function toCompetitionDate(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  const normalized = raw.includes(" ") ? raw.replace(" ", "T") : raw;
  const date = new Date(normalized);

  return Number.isNaN(date.getTime()) ? null : date;
}

export function toDateOnly(value) {
  const date = toCompetitionDate(value);
  if (!date) return null;

  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function todayDateOnly() {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
}

export function formatDateFR(value) {
  const date = toCompetitionDate(value);
  if (!date) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatDateLongFR(value) {
  const date = toCompetitionDate(value);
  if (!date) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function safeDisciplines(raw) {
  try {
    if (Array.isArray(raw)) return raw.filter(Boolean);

    if (typeof raw === "string" && raw.trim() === "") return [];

    let parsed = typeof raw === "string" ? JSON.parse(raw) : raw;

    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }

    if (Array.isArray(parsed)) return parsed.filter(Boolean);

    if (parsed) return [String(parsed)];

    return [];
  } catch {
    return raw ? [String(raw)] : ["Judo"];
  }
}

export function getCompetitionTemporalStatus(competition) {
  const date = toDateOnly(competition?.date_competition);
  const today = todayDateOnly();

  if (!date) {
    return {
      key: "unknown",
      label: "Date inconnue",
      tone:
        "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    };
  }

  if (date < today) {
    return {
      key: "past",
      label: "Passée",
      tone:
        "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    };
  }

  if (date.getTime() === today.getTime()) {
    return {
      key: "today",
      label: "Aujourd’hui",
      tone:
        "bg-amber-100 text-amber-700 dark:bg-amber-950/70 dark:text-amber-300",
    };
  }

  return {
    key: "upcoming",
    label: "À venir",
    tone:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/70 dark:text-emerald-300",
  };
}

export function getLevelLabel(level) {
  const isNumber = /^\d+$/.test(String(level || ""));
  return isNumber ? `Niveau ${level}` : level || "—";
}

export function getLevelTone(level) {
  const normalized = normalizeText(level);

  if (normalized.includes("regional") || normalized.includes("ligue")) {
    return "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/70 dark:text-indigo-300";
  }

  if (normalized.includes("departement")) {
    return "bg-blue-100 text-blue-700 dark:bg-blue-950/70 dark:text-blue-300";
  }

  if (normalized.includes("national")) {
    return "bg-violet-100 text-violet-700 dark:bg-violet-950/70 dark:text-violet-300";
  }

  return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";
}

export function getOrganizerLabel(organizer, organizerId) {
  if (organizer === undefined) return "Chargement…";

  if (!organizer) {
    return organizerId ? `#${organizerId}` : "Non renseigné";
  }

  const fullName = `${organizer.prenom || ""} ${organizer.nom || ""}`.trim();

  return fullName || organizer.email || `#${organizerId}`;
}

export function getPresencePercent(stats) {
  const totalInscrits = Number(stats?.totalInscrits || 0);
  const totalPresents = Number(stats?.totalPresents || 0);

  if (!totalInscrits) return null;

  return ((totalPresents / totalInscrits) * 100).toFixed(1);
}

export function openCompetitionPublicPage(competitionId) {
  if (!competitionId) return;

  window.open(
    `/competition/${competitionId}`,
    "_blank",
    "noopener,noreferrer"
  );
}

function csvCell(value) {
  const text = String(value ?? "").replace(/\r?\n|\r/g, " ");
  return `"${text.replace(/"/g, '""')}"`;
}

export function buildCompetitionsCSV(competitions = [], organizerMap = {}) {
  const headers = [
    "id",
    "date_competition",
    "nom_competition",
    "lieu_competition",
    "level_competition",
    "disciplines",
    "organisateur",
    "is_active",
    "is_public",
  ];

  const rows = competitions.map((competition) => {
    const organizer = organizerMap[competition.organisateur_competition];
    const organizerLabel = getOrganizerLabel(
      organizer,
      competition.organisateur_competition
    );

    return [
      competition.id,
      toCompetitionDate(competition.date_competition)?.toISOString() || "",
      competition.nom_competition || "",
      competition.lieu_competition || "",
      competition.level_competition || "",
      safeDisciplines(competition.disciplines_competition).join(" / "),
      organizerLabel,
      competition.is_active ? "1" : "0",
      competition.is_public ? "1" : "0",
    ];
  });

  return [
    headers.map(csvCell).join(";"),
    ...rows.map((row) => row.map(csvCell).join(";")),
  ].join("\n");
}

export function downloadCSV(filename, content) {
  const blob = new Blob(["\uFEFF" + content], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
}